/**
 * Reload-loop guard's persisted state: a bounded-retry `ReloadAttemptRecord`
 * (see AppFreshnessGuard/versionFreshness.ts) kept in sessionStorage so a stuck
 * reload budget doesn't survive past the tab.
 */
import type { ReloadAttemptRecord } from '@/components/guards/AppFreshnessGuard/versionFreshness'
import { safeLocalStorage } from '@/services/safeLocalStorage'

const STORAGE_KEY = 'nopressure:versionReloadAttempt'

/** Validates a parsed value's shape before trusting it; null on anything unexpected. */
function parseReloadAttemptRecord(value: unknown): ReloadAttemptRecord | null {
  if (typeof value !== 'object' || value === null) return null

  const { target, attempts, lastAttemptAt } = value as {
    target?: unknown
    attempts?: unknown
    lastAttemptAt?: unknown
  }
  if (typeof target !== 'string' || typeof attempts !== 'number' || typeof lastAttemptAt !== 'number') return null

  return { target, attempts, lastAttemptAt }
}

export const versionReloadGuard = {
  /** Returns the persisted reload-attempt record, or null when absent/malformed. */
  get(): ReloadAttemptRecord | null {
    const stored = safeLocalStorage.get(STORAGE_KEY, 'session')
    if (stored === null) return null

    try {
      return parseReloadAttemptRecord(JSON.parse(stored))
    } catch {
      return null
    }
  },

  /** Persists the reload-attempt record for the target version being awaited. */
  set(record: ReloadAttemptRecord): void {
    safeLocalStorage.set(STORAGE_KEY, JSON.stringify(record), 'session')
  },

  /** Clears the persisted record — the next check starts with a fresh budget. */
  clear(): void {
    safeLocalStorage.remove(STORAGE_KEY, 'session')
  },

  /** True when sessionStorage is writable; false means the guard must skip auto-reload. */
  isAvailable(): boolean {
    return safeLocalStorage.isAvailable('session')
  },
}
